import type { Dispatch, MutableRefObject, SetStateAction } from "react";

type RefPair = { refKey: string; refVisible: boolean };

export function createSeriesToggleHandler<SeriesType extends { [key: string]: boolean }>(
  setVisibleSeries: Dispatch<SetStateAction<SeriesType>>,
  lastClickedSeriesId?: MutableRefObject<string | undefined>,
  refPair?: RefPair
): (this: Highcharts.Series, event: Highcharts.SeriesLegendItemClickEventObject) => boolean {

  const getSeriesId = (series: Highcharts.Series): string | undefined => {
    return series.options?.id ?? series.userOptions?.id;
  };

  return function handleSeriesVisibilityToggle(
    this: Highcharts.Series,
    event: Highcharts.SeriesLegendItemClickEventObject
  ): boolean {
    const { id: seriesId } = event.target.userOptions;
    const { visible: isSeriesVisible, chart } = this;

    const legendSeries = chart.series.filter((series) => series.options?.showInLegend !== false);
    const visibleLegendSeries = legendSeries.filter((series) => series.visible);

    // Update last clicked series ID if provided
    if (lastClickedSeriesId) {
      lastClickedSeriesId.current = seriesId;
    }

    if (event.browserEvent?.altKey) {
      const isAlreadyIsolated =
        isSeriesVisible &&
        visibleLegendSeries.length === 1 &&
        getSeriesId(visibleLegendSeries[0]) === seriesId;

      setVisibleSeries((prevVisibility) => {
        const nextVisibility = { ...prevVisibility } as Record<string, boolean>;
        legendSeries.forEach((series) => {
          const id = getSeriesId(series);
          if (id !== undefined) {
            nextVisibility[id] = isAlreadyIsolated || id === seriesId;
          }
        });
        return nextVisibility as SeriesType;
      });

      event.preventDefault();
      return false;
    }

    // Prevent hiding the last visible series
    const isLastLegendVisible = isSeriesVisible && visibleLegendSeries.length < 2;

    const isOnlyBaseRefPairVisible =
      isSeriesVisible &&
      !!refPair?.refKey &&
      refPair.refVisible === true &&
      visibleLegendSeries.length <= 2 &&
      visibleLegendSeries.every((series) => {
        const id = getSeriesId(series);
        return id === seriesId || id === refPair.refKey;
      });

    if (isLastLegendVisible || isOnlyBaseRefPairVisible) {
      event.preventDefault();
      return false;
    }

    // Toggle series visibility
    setVisibleSeries((prevVisibility) => ({
      ...prevVisibility,
      [seriesId as string]: !prevVisibility[seriesId as string],
    }));

    // Prevent Highcharts default visibility update
    return false;
  };
}
